import {
  OwFFormFieldKey,
  OwFFormFieldType,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/consts/index";
import {
  EXPLANATION_POSITION,
  IOwFFormFieldSchema,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/OwFFormField";
import { useGetOwFApplicationStoreType } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useGetOwFApplicationStoreType";
import { useIsNMIApplication } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsNMIApplication";
import { useIsSoftCreditCheckApplication } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsSoftCreditCheckApplication";
import { useOwFIDVTierLogic } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useOwFIDVTierLogic";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { useStrictLocalization } from "@faire/web/common/localization";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireApplication";

export const useAdditionalInfo = (
  formData: IRetailerLaunchWithFaireApplication
) => {
  const { strictLocalize } = useStrictLocalization();
  const { isNMIApplication } = useIsNMIApplication();
  const { owfStoreType } = useGetOwFApplicationStoreType();
  const { retailer } = useRetailer();

  const isRetailerOther =
    retailer?.business_category === IRetailer.BusinessCategory.BUSINESS_OTHER;
  const owfFormStoreType = isNMIApplication
    ? owfStoreType
    : formData.best_store_type;
  const retailerBusinessType = isRetailerOther
    ? (owfFormStoreType ?? IRetailer.BusinessCategory.ONLINE_ONLY)
    : retailer?.business_category;
  const isOnlineOnly =
    retailerBusinessType === IRetailer.BusinessCategory.ONLINE_ONLY;

  const visibleAdditionalInfoKeys = useAdditionalInfoKeys({
    isOnlineOnly,
  });

  const additionalDocExplanation: string = (() => {
    switch (retailerBusinessType) {
      case IRetailer.BusinessCategory.ONLINE_ONLY:
        return strictLocalize({
          defaultMessage:
            "E.g. your story, business plan, bank statements, resume, etc.",
          description: {
            text: "Explanation text for a form field asking for additional document on their store info",
          },
        });
      case IRetailer.BusinessCategory.POP_UP:
        return strictLocalize({
          defaultMessage:
            "E.g. Lease agreement, secured spaces at events, pop-up space photos, etc.",
          description: {
            text: "Explanation text for a form field asking for additional document on their store info",
          },
        });
      default:
        return strictLocalize({
          defaultMessage:
            "E.g. your lease agreement or proof of ownership, store photos, store video, etc.",
          description: {
            text: "Explanation text for a form field asking for additional document on their store info",
          },
        });
    }
  })();

  const getDocumentsText = () => {
    if (isOnlineOnly) {
      strictLocalize({
        defaultMessage:
          "Please include any additional documents that will help us get to know your store better.",
      });
    }

    return strictLocalize({
      defaultMessage:
        "Please include any additional documents that will help us verify your storefront.",
    });
  };

  const getAdditionalInformationLabelText = () => {
    return isNMIApplication
      ? strictLocalize({
          defaultMessage:
            "Is there anything else you’d like to share with us? (optional)",
          description: {
            text: "Ask retailer for any extra info to share in the Open with Faire application",
            img: "https://cdn.faire.com/fastly/9067c2399494c2fb8d6f91320693def1b246e24fe1f725f94bc2853a64f43651.png",
          },
        })
      : strictLocalize({
          defaultMessage: "Is there anything else you’d like to share with us?",
          description: {
            text: "Ask retailer for any extra info to share in the Open with Faire application",
          },
        });
  };

  const additionalInfoFields: IOwFFormFieldSchema[] =
    visibleAdditionalInfoKeys.map((key) => {
      switch (key) {
        case "website_url":
          return {
            fieldType: OwFFormFieldType.INPUT,
            fieldName: "website_url",
            labelText: strictLocalize({
              defaultMessage: "What’s your store’s website?",
              description: {
                text: "Ask retailers to provide the website for their store",
              },
            }),
            required: false,
          };
        case "social_media":
          return {
            fieldType: OwFFormFieldType.TEXTAREA,
            fieldName: "social_media",
            labelText: strictLocalize({
              defaultMessage: "Share any social media links for your store.",
              description: {
                text: "Ask retailers to provide the social media link for their store",
              },
            }),
            explanation: strictLocalize({
              defaultMessage: "E.g. instagram.com/generalstore",
              description: {
                text: "Explanation text for a form field asking for retailer's social media links",
              },
            }),
            explanationPosition: EXPLANATION_POSITION.TOP,
            required: false,
          };
        case "documents":
          return {
            fieldType: OwFFormFieldType.FILE,
            fieldName: "documents",
            labelText: getDocumentsText(),
            explanation: additionalDocExplanation,
            required: false,
          };
        default:
        case "additional_information":
          return {
            fieldType: OwFFormFieldType.TEXTAREA,
            fieldName: "additional_information",
            labelText: getAdditionalInformationLabelText(),
            explanation: isNMIApplication
              ? strictLocalize({
                  defaultMessage:
                    "E.g. opening new locations, moving from online to physical stores, etc.",
                  description: {
                    text: "Ask retailer for any extra info to share in the Open with Faire application",
                    img: "https://cdn.faire.com/fastly/9067c2399494c2fb8d6f91320693def1b246e24fe1f725f94bc2853a64f43651.png",
                  },
                })
              : undefined,
            explanationPosition: EXPLANATION_POSITION.BOTTOM,
            required: false,
            maxLength: 1024,
          };
      }
    });

  return { visibleAdditionalInfoKeys, additionalInfoFields };
};

const useAdditionalInfoKeys = ({
  isOnlineOnly,
}: {
  isOnlineOnly: boolean;
}): OwFFormFieldKey[] => {
  const { isAnyTierActive } = useOwFIDVTierLogic();
  const { isNMIApplication } = useIsNMIApplication();
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();

  if (isNMIApplication) {
    return ["additional_information"];
  }
  if (isSoftCreditCheckApplication) {
    return [];
  }

  let visibleAdditionalInfoKeys: OwFFormFieldKey[] = [];

  if (isAnyTierActive) {
    visibleAdditionalInfoKeys = ["additional_information"];
  } else {
    visibleAdditionalInfoKeys = ["documents", "additional_information"];
    if (!isOnlineOnly) {
      visibleAdditionalInfoKeys = (
        ["website_url", "social_media"] as OwFFormFieldKey[]
      ).concat(visibleAdditionalInfoKeys);
    }
  }

  return visibleAdditionalInfoKeys;
};
